import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import OranHub from "@/assets/home/OranHub.png";
import OranSim from "@/assets/home/OranSim.png";
import OranGen from "@/assets/home/OranGen.png";
import OranMed from "@/assets/home/OranMed.png";

const engineLogos: Record<string, string> = {
  OranHub: OranHub,
  OranSim: OranSim,
  OranGen: OranGen,
  OranMed: OranMed,
};

export type Engine = { name: string; tag: string; line: string; href?: string };

export function EngineGrid({ engines, t }: { engines: Engine[]; t: (en: string, zh: string) => string }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 1200, h: 600 });

  useEffect(() => {
    if (!stageRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width && height) setSize({ w: width, h: height });
    });
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  const archY = [36, -24, -24, 36];

  const isSegActive = (i: number) =>
    hovered !== null && (hovered === i || hovered === (i + 1) % engines.length);

  const ptsPct = [
    { x: 0.125, y: 0.25 },
    { x: 0.375, y: 0.11 },
    { x: 0.625, y: 0.11 },
    { x: 0.875, y: 0.25 },
  ];
  const px = (p: { x: number; y: number }) => ({ x: p.x * size.w, y: p.y * size.h });
  const P = ptsPct.map(px);
  const segs = [
    `M ${P[0].x} ${P[0].y} Q ${0.25 * size.w} ${-0.07 * size.h} ${P[1].x} ${P[1].y}`,
    `M ${P[1].x} ${P[1].y} Q ${0.5 * size.w} ${-0.03 * size.h} ${P[2].x} ${P[2].y}`,
    `M ${P[2].x} ${P[2].y} Q ${0.75 * size.w} ${-0.07 * size.h} ${P[3].x} ${P[3].y}`,
    `M ${P[3].x} ${P[3].y} C ${1.1 * size.w} ${1.05 * size.h}, ${-0.1 * size.w} ${1.05 * size.h}, ${P[0].x} ${P[0].y}`,
  ];

  return (
    <div ref={stageRef} className="engine-stage relative pb-24 lg:pb-32" onMouseLeave={() => setHovered(null)}>
      <svg
        aria-hidden
        className="engine-arcs absolute inset-x-0 top-0 hidden lg:block pointer-events-none"
        width={size.w}
        height={size.h}
        viewBox={`0 0 ${size.w} ${size.h}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="engine-particle">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="35%" stopColor="white" stopOpacity="0.85" />
            <stop offset="70%" stopColor="white" stopOpacity="0.15" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="engine-arc-star">
            <stop offset="0%" stopColor="white" stopOpacity="1" />
            <stop offset="40%" stopColor="white" stopOpacity="0.6" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </radialGradient>
          {segs.map((_d, i) => {
            const a = P[i];
            const b = P[(i + 1) % engines.length];
            return (
              <linearGradient
                key={`lg-${i}`}
                id={`engine-arc-grad-${i}`}
                gradientUnits="userSpaceOnUse"
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
              >
                <stop offset="0%" stopColor="white" stopOpacity="0" />
                <stop offset="55%" stopColor="white" stopOpacity="0.35" />
                <stop offset="100%" stopColor="white" stopOpacity="0.95" />
              </linearGradient>
            );
          })}
          {segs.map((d, i) => (
            <path key={i} id={`engine-arc-${i}`} d={d} />
          ))}
        </defs>

        {segs.map((_d, i) => (
          <use
            key={`g-${i}`}
            href={`#engine-arc-${i}`}
            className={`engine-arc ${isSegActive(i) ? "engine-arc--active" : ""}`}
            stroke={`url(#engine-arc-grad-${i})`}
          />
        ))}

        {segs.map((_d, i) => {
          const b = P[(i + 1) % engines.length];
          return (
            <g key={`s-${i}`} className={`engine-arc-endpoint ${isSegActive(i) ? "is-active" : ""}`}>
              <circle cx={b.x} cy={b.y} r={14} fill="url(#engine-arc-star)" />
              <circle cx={b.x} cy={b.y} r={2.4} fill="white" />
            </g>
          );
        })}

        {segs.map((_d, i) => {
          if (!isSegActive(i)) return null;
          const dur = i === segs.length - 1 ? 7 : 3.5;
          const offsets = [0.0, 0.08, 0.27, 0.34, 0.55, 0.71, 0.88];
          const radii = [7, 3.5, 9, 2.5, 5, 8, 4];
          const opas = [1.0, 0.85, 1.0, 0.7, 0.9, 1.0, 0.85];
          const particles = offsets.map((f, k) => ({
            r: radii[k],
            delay: f * dur,
            o: opas[k],
          }));
          return (
            <g key={`p-${i}`} className="engine-particles">
              {particles.map((p, k) => (
                <circle key={k} r={p.r} fill="url(#engine-particle)" opacity={p.o}>
                  <animateMotion dur={`${dur}s`} begin={`${p.delay}s`} repeatCount="indefinite" rotate="auto">
                    <mpath href={`#engine-arc-${i}`} />
                  </animateMotion>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>

      <div className="relative grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {engines.map((e, i) => {
          const isHover = hovered === i;
          const isAdjacent =
            hovered !== null && (Math.abs(hovered - i) === 1 || Math.abs(hovered - i) === engines.length - 1);
          return (
            <div
              key={e.name}
              className="engine-arch-slot"
              style={{
                ["--arch-y" as string]: `${archY[i]}px`,
                animationDelay: `${i * 0.9}s`,
              }}
            >
              <div
                onMouseEnter={() => setHovered(i)}
                className={`engine-card group relative glass nebula-border p-10 min-h-[420px] flex flex-col items-center text-center ${
                  isAdjacent ? "engine-card--adjacent" : ""
                } ${isHover ? "engine-card--active" : ""}`}
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <span aria-hidden className="engine-sheen" />
                <span aria-hidden className="engine-spot" />

                {/* <span className="absolute top-5 right-6 text-xs text-muted-foreground tracking-widest transition-colors duration-500 group-hover:text-[hsl(var(--brand))]">
                  0{i + 1}
                </span> */}

                <img
                  src={engineLogos[e.name] || ""}
                  alt={`${e.name} logo`}
                  className="engine-glyph h-20 w-20 mb-8 object-contain"
                />

                <h3 className="text-3xl font-semibold transition-transform duration-500 group-hover:-translate-y-0.5">
                  {e.name}
                </h3>
                <p className="text-sm italic text-muted-foreground mt-2 mb-5">{e.tag}</p>
                <p className="text-foreground/90 leading-relaxed">{e.line}</p>
                {e.href && /^https?:\/\//.test(e.href) ? (
                  <a
                    href={e.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto pt-6 inline-flex items-center gap-1 text-sm brand-text font-medium group/link"
                  >
                    {t("Learn more", "了解更多")}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                  </a>
                ) : (
                  <a
                    href={e.href || "#"}
                    className="mt-auto pt-6 inline-flex items-center gap-1 text-sm brand-text font-medium group/link"
                  >
                    {t("Learn more", "了解更多")}
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
